/**
 * QaDashboard — QA testing home at /admin/qa
 *
 * Lists test batches grouped by release week with progress bars.
 * Each item has an info button opening QaTestDetailModal.
 * Quick traffic-light taps: green = save, yellow/red = modal.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { FlaskConical, Info, ChevronDown, ChevronRight, ExternalLink, Users } from 'lucide-react';
import QaTestDetailModal from './QaTestDetailModal';
import { endpoints, qaHeaders } from './config';

// ── Helpers ────────────────────────────────────────────

function getTesterId() {
  let id = localStorage.getItem('kuutio-qa-tester-id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('kuutio-qa-tester-id', id);
  }
  return id;
}

function getTesterName() {
  return localStorage.getItem('kuutio-qa-tester-name') || '';
}

function currentWeek() {
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// ── Styled Components ─────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
`;

const Content = styled.div`
  padding: 32px 48px;
  max-width: 900px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;

  svg { width: 28px; height: 28px; color: #c084fc; }

  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    margin: 0;
  }
`;

const WeekBadge = styled.span`
  font-size: 13px;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 12px;
  border-radius: 6px;
  margin-left: auto;
`;

const OverallProgress = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ProgressStat = styled.div`
  text-align: center;
`;

const ProgressNumber = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${props => props.$color || '#fff'};
  font-variant-numeric: tabular-nums;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 2px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  overflow: hidden;
  display: flex;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$pct}%;
  transition: width 0.4s;
`;

// ── Batch ─────────────────────────────────

const BatchCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  margin-bottom: 20px;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${props => props.$delay || '0s'};
  animation-fill-mode: backwards;
`;

const BatchHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  text-align: left;

  &:hover { background: rgba(255, 255, 255, 0.02); }
`;

const BatchWeek = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #c084fc;
  background: rgba(147, 51, 234, 0.15);
  padding: 3px 10px;
  border-radius: 5px;
  flex-shrink: 0;
`;

const BatchTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  flex: 1;
`;

const BatchStats = styled.span`
  font-size: 12px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MiniBar = styled.div`
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  display: flex;
`;

const MiniBarFill = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$pct}%;
`;

const BatchBody = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const BatchSummary = styled.div`
  padding: 10px 20px;
  font-size: 13px;
  color: #888;
  background: rgba(255, 255, 255, 0.01);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const ViewsList = styled.div`
  padding: 4px 20px 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ViewChip = styled.button`
  font-size: 11px;
  color: #aaa;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover { color: #fff; border-color: rgba(147, 51, 234, 0.4); }
  svg { width: 10px; height: 10px; }
`;

// ── Item Row ──────────────────────────────

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: background 0.1s;

  &:hover { background: rgba(255, 255, 255, 0.02); }
  &:last-child { border-bottom: none; }
`;

const ItemTitle = styled.span`
  font-size: 13px;
  color: #ddd;
  flex: 1;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    ${props => props.$clickable && `
      color: #c084fc;
      text-decoration: underline;
      text-decoration-color: rgba(192, 132, 252, 0.4);
      text-underline-offset: 2px;
    `}
  }
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props =>
    props.$status === 'green' ? '#22c55e'
    : props.$status === 'yellow' ? '#facc15'
    : props.$status === 'red' ? '#ef4444'
    : 'rgba(255, 255, 255, 0.1)'};
  border: ${props => !props.$status ? '2px solid rgba(255, 255, 255, 0.15)' : 'none'};
  flex-shrink: 0;
`;

const QuickBtn = styled.button`
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;
  min-width: 28px;
  text-align: center;

  background: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.2)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.2)'
    : 'rgba(239, 68, 68, 0.2)'
    : 'rgba(255, 255, 255, 0.03)'};
  border-color: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.4)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.4)'
    : 'rgba(239, 68, 68, 0.4)'
    : 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$active
    ? props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'
    : '#555'};

  &:hover {
    color: ${props =>
      props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'};
  }
`;

const InfoBtn = styled.button`
  background: none;
  border: none;
  color: #555;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  &:hover { color: #c084fc; background: rgba(147, 51, 234, 0.1); }
  svg { width: 16px; height: 16px; }
`;

// ── Dashboard Overview ───────────────────

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 32px;
`;

const DashCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 20px;
`;

const DashCardTitle = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { width: 14px; height: 14px; color: #c084fc; }
`;

const PieChartRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const PieLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #ccc;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const LegendCount = styled.span`
  margin-left: auto;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${props => props.$color || '#fff'};
`;

const TesterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TesterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #ccc;
`;

const TesterName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TesterMiniBar = styled.div`
  width: 80px;
  height: 6px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  overflow: hidden;
  display: flex;
`;

const TesterMiniBarFill = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$pct}%;
`;

const TesterCount = styled.span`
  font-size: 12px;
  color: #888;
  font-variant-numeric: tabular-nums;
  min-width: 32px;
  text-align: right;
`;

const ReadinessGauge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ReadinessLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ReadinessBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
`;

const ReadinessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #888;
`;

const ReadinessBarTrack = styled.div`
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
`;

const ReadinessBarFill = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$pct}%;
  border-radius: 2px;
  transition: width 0.4s;
`;

function GaugeRing({ score, color, size = 110 }) {
  const r = (size - 12) / 2;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const progress = (score / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={`${progress} ${circumference - progress}`}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text x={cx} y={cy - 6} textAnchor="middle" fill="#fff" fontSize="28" fontWeight="800">{score}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="#666" fontSize="10">/100</text>
    </svg>
  );
}

function PieChart({ green, yellow, red, untested, size = 100 }) {
  const total = green + yellow + red + untested;
  if (total === 0) return null;

  const slices = [
    { count: green, color: '#22c55e' },
    { count: yellow, color: '#facc15' },
    { count: red, color: '#ef4444' },
    { count: untested, color: 'rgba(255,255,255,0.15)' },
  ].filter(s => s.count > 0);

  const r = size / 2;
  const cx = r, cy = r;
  const paths = [];
  let startAngle = -Math.PI / 2;

  for (const slice of slices) {
    const angle = (slice.count / total) * 2 * Math.PI;
    const endAngle = startAngle + angle;
    const largeArc = angle > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);

    if (slices.length === 1) {
      paths.push(<circle key={slice.color} cx={cx} cy={cy} r={r} fill={slice.color} />);
    } else {
      paths.push(
        <path
          key={slice.color + startAngle}
          d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
          fill={slice.color}
        />
      );
    }
    startAngle = endAngle;
  }

  // Center label
  const pctDone = total > 0 ? Math.round(((green + yellow + red) / total) * 100) : 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {paths}
      <circle cx={cx} cy={cy} r={r * 0.6} fill="#0a0a0a" />
      <text x={cx} y={cy - 4} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="800">{pctDone}%</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="#666" fontSize="10">testattu</text>
    </svg>
  );
}

// ── Component ───────��─────────────────────

export default function QaDashboard({
  items: QA_TEST_ITEMS = [],     // Test items array (consumer provides)
  batches: QA_BATCHES = {},      // Batch metadata (optional)
  header = null,                  // Optional header component (e.g. AdminHeader)
}) {
  const navigate = useNavigate();
  const [results, setResults] = useState({});  // { itemId: [{ tester_id, status }] }
  const [expandedBatches, setExpandedBatches] = useState({});
  const [modalItem, setModalItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const testerId = getTesterId();
  const week = currentWeek();
  const instanceId = new URLSearchParams(window.location.search).get('instance')
    || new URLSearchParams(window.location.search).get('kid');

  // Group items by release
  const batches = {};
  for (const item of QA_TEST_ITEMS) {
    const release = item.release || 'unscheduled';
    if (!batches[release]) batches[release] = [];
    batches[release].push(item);
  }
  // Sort releases newest first
  const sortedReleases = Object.keys(batches).sort().reverse();

  // Auto-expand current week's batch
  useEffect(() => {
    setExpandedBatches(prev => ({ ...prev, [week]: true }));
    // Also expand latest batch
    if (sortedReleases[0]) {
      setExpandedBatches(prev => ({ ...prev, [sortedReleases[0]]: true }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed items to DB on mount (ensures descriptions stay in sync)
  useEffect(() => {
    if (QA_TEST_ITEMS.length === 0) return;
    fetch(endpoints.itemsSeed(), {
      method: 'POST',
      headers: qaHeaders(),
      body: JSON.stringify({ items: QA_TEST_ITEMS }),
    }).catch(() => {}); // silent — works without backend too
  }, []);

  // Fetch all results
  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(endpoints.results(week), { headers: qaHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const map = {};
      for (const item of (data.items || [])) {
        map[item.id] = item.results || [];
      }
      setResults(map);
    } catch {
      // Silent — static items still show
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // My status for an item
  const myStatus = (itemId) => {
    const r = results[itemId] || [];
    return r.find(x => x.tester_id === testerId)?.status || null;
  };

  const myComment = (itemId) => {
    const r = results[itemId] || [];
    return r.find(x => x.tester_id === testerId)?.comment || '';
  };

  // Save result
  const saveResult = useCallback(async (itemId, status, comment) => {
    try {
      await fetch(endpoints.resultsSave(), {
        method: 'PUT',
        headers: qaHeaders(),
        body: JSON.stringify({
          itemId, status, comment: comment || undefined,
          week, testerId,
          testerName: getTesterName() || undefined,
          instanceId: instanceId || undefined,
          browserInfo: { userAgent: navigator.userAgent, viewport: `${window.innerWidth}x${window.innerHeight}` },
        }),
      });
      fetchResults();
    } catch (err) {
      console.error('QA save error:', err);
    }
  }, [week, testerId, instanceId, fetchResults]);

  // Quick green tap — save immediately
  const handleQuickGreen = (itemId) => {
    saveResult(itemId, 'green', '');
  };

  // Yellow/red/info — open modal
  const handleOpenModal = (item, presetStatus) => {
    setModalItem({ item, presetStatus });
  };

  // Toggle batch
  const toggleBatch = (release) => {
    setExpandedBatches(prev => ({ ...prev, [release]: !prev[release] }));
  };

  // Navigate to view
  const navigateToView = (route) => {
    const sep = route.includes('?') ? '&' : '?';
    navigate(instanceId ? `${route}${sep}instance=${instanceId}` : route);
  };

  // Overall stats (my results)
  const totalItems = QA_TEST_ITEMS.length;
  const testedItems = QA_TEST_ITEMS.filter(i => myStatus(i.id)).length;
  const greenItems = QA_TEST_ITEMS.filter(i => myStatus(i.id) === 'green').length;
  const yellowItems = QA_TEST_ITEMS.filter(i => myStatus(i.id) === 'yellow').length;
  const redItems = QA_TEST_ITEMS.filter(i => myStatus(i.id) === 'red').length;

  // Aggregate stats across ALL testers
  const allTesters = useMemo(() => {
    const testerMap = {}; // { testerId: { name, green, yellow, red, total } }
    for (const itemResults of Object.values(results)) {
      for (const r of itemResults) {
        if (!testerMap[r.tester_id]) {
          testerMap[r.tester_id] = { name: r.tester_name || r.tester_id?.slice(0, 8), green: 0, yellow: 0, red: 0, total: 0 };
        }
        testerMap[r.tester_id][r.status]++;
        testerMap[r.tester_id].total++;
      }
    }
    return Object.entries(testerMap)
      .map(([id, stats]) => ({ id, ...stats }))
      .sort((a, b) => b.total - a.total);
  }, [results]);

  // Aggregate: per-item worst status across all testers
  const aggregateStats = useMemo(() => {
    let aggGreen = 0, aggYellow = 0, aggRed = 0, aggUntested = 0;
    for (const item of QA_TEST_ITEMS) {
      const itemResults = results[item.id] || [];
      if (itemResults.length === 0) { aggUntested++; continue; }
      const hasRed = itemResults.some(r => r.status === 'red');
      const hasYellow = itemResults.some(r => r.status === 'yellow');
      if (hasRed) aggRed++;
      else if (hasYellow) aggYellow++;
      else aggGreen++;
    }
    return { green: aggGreen, yellow: aggYellow, red: aggRed, untested: aggUntested };
  }, [results]);

  // Readiness score (0-100)
  const readiness = useMemo(() => {
    const total = QA_TEST_ITEMS.length;
    if (total === 0) return { score: 0, coverage: 0, quality: 0, depth: 0, label: 'Ei dataa', color: '#555' };

    const tested = aggregateStats.green + aggregateStats.yellow + aggregateStats.red;
    const coverage = tested / total;
    const quality = tested > 0
      ? (aggregateStats.green * 1.0 + aggregateStats.yellow * 0.5 + aggregateStats.red * 0) / tested
      : 0;

    // Depth: average testers per tested item, target 3
    const TARGET_TESTERS = 3;
    let totalTesters = 0;
    let testedCount = 0;
    for (const item of QA_TEST_ITEMS) {
      const r = results[item.id] || [];
      if (r.length > 0) {
        totalTesters += r.length;
        testedCount++;
      }
    }
    const avgTesters = testedCount > 0 ? totalTesters / testedCount : 0;
    const depth = Math.min(1, avgTesters / TARGET_TESTERS);

    // Quality only counts proportional to coverage — 1 green test ≠ 35 points
    const score = Math.round(coverage * 50 + coverage * quality * 35 + depth * 15);

    let label, color;
    if (score >= 90) { label = 'Ship it'; color = '#22c55e'; }
    else if (score >= 70) { label = 'Tarkista'; color = '#facc15'; }
    else { label = 'Ei valmis'; color = '#ef4444'; }

    return { score, coverage: Math.round(coverage * 100), quality: Math.round(quality * 100), depth: Math.round(depth * 100), label, color };
  }, [aggregateStats, results]);

  // Latest batch stats
  const latestBatch = sortedReleases[0];
  const latestBatchItems = batches[latestBatch] || [];
  const latestBatchStats = useMemo(() => {
    let g = 0, y = 0, r = 0, u = 0;
    for (const item of latestBatchItems) {
      const itemResults = results[item.id] || [];
      if (itemResults.length === 0) { u++; continue; }
      const hasRed = itemResults.some(res => res.status === 'red');
      const hasYellow = itemResults.some(res => res.status === 'yellow');
      if (hasRed) r++;
      else if (hasYellow) y++;
      else g++;
    }
    return { green: g, yellow: y, red: r, untested: u };
  }, [results, latestBatchItems]);

  return (
    <Container>
      {header}

      <Content>
        <PageHeader>
          <FlaskConical />
          <h1>QA Dashboard</h1>
          <WeekBadge>{week}</WeekBadge>
        </PageHeader>

        <OverallProgress>
          <ProgressStat>
            <ProgressNumber>{testedItems}/{totalItems}</ProgressNumber>
            <ProgressLabel>Testattu</ProgressLabel>
          </ProgressStat>
          <ProgressBarTrack>
            <ProgressBarFill $color="#22c55e" $pct={totalItems ? (greenItems / totalItems) * 100 : 0} />
            <ProgressBarFill $color="#facc15" $pct={totalItems ? (yellowItems / totalItems) * 100 : 0} />
            <ProgressBarFill $color="#ef4444" $pct={totalItems ? (redItems / totalItems) * 100 : 0} />
          </ProgressBarTrack>
          <ProgressStat>
            <ProgressNumber $color="#22c55e">{greenItems}</ProgressNumber>
            <ProgressLabel>OK</ProgressLabel>
          </ProgressStat>
          <ProgressStat>
            <ProgressNumber $color="#facc15">{yellowItems}</ProgressNumber>
            <ProgressLabel>Osittain</ProgressLabel>
          </ProgressStat>
          <ProgressStat>
            <ProgressNumber $color="#ef4444">{redItems}</ProgressNumber>
            <ProgressLabel>Virhe</ProgressLabel>
          </ProgressStat>
        </OverallProgress>

        <DashboardGrid>
          <DashCard>
            <DashCardTitle>
              <FlaskConical />
              Valmius
            </DashCardTitle>
            <ReadinessGauge>
              <GaugeRing score={readiness.score} color={readiness.color} />
              <ReadinessLabel $color={readiness.color}>{readiness.label}</ReadinessLabel>
              <ReadinessBreakdown>
                <ReadinessRow>
                  <span>Kattavuus</span>
                  <ReadinessBarTrack>
                    <ReadinessBarFill $color="#c084fc" $pct={readiness.coverage} />
                  </ReadinessBarTrack>
                  <span>{readiness.coverage}%</span>
                </ReadinessRow>
                <ReadinessRow>
                  <span>Laatu</span>
                  <ReadinessBarTrack>
                    <ReadinessBarFill $color={readiness.quality >= 80 ? '#22c55e' : readiness.quality >= 50 ? '#facc15' : '#ef4444'} $pct={readiness.quality} />
                  </ReadinessBarTrack>
                  <span>{readiness.quality}%</span>
                </ReadinessRow>
                <ReadinessRow>
                  <span>Syvyys</span>
                  <ReadinessBarTrack>
                    <ReadinessBarFill $color="#60a5fa" $pct={readiness.depth} />
                  </ReadinessBarTrack>
                  <span>{readiness.depth}%</span>
                </ReadinessRow>
              </ReadinessBreakdown>
            </ReadinessGauge>
          </DashCard>

          <DashCard>
            <DashCardTitle>
              <FlaskConical />
              Kaikki testit — {week}
            </DashCardTitle>
            <PieChartRow>
              <PieChart
                green={aggregateStats.green}
                yellow={aggregateStats.yellow}
                red={aggregateStats.red}
                untested={aggregateStats.untested}
              />
              <PieLegend>
                <LegendItem>
                  <LegendDot $color="#22c55e" />
                  OK
                  <LegendCount $color="#22c55e">{aggregateStats.green}</LegendCount>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#facc15" />
                  Osittain
                  <LegendCount $color="#facc15">{aggregateStats.yellow}</LegendCount>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#ef4444" />
                  Virhe
                  <LegendCount $color="#ef4444">{aggregateStats.red}</LegendCount>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="rgba(255,255,255,0.15)" />
                  Testaamatta
                  <LegendCount>{aggregateStats.untested}</LegendCount>
                </LegendItem>
              </PieLegend>
            </PieChartRow>
          </DashCard>

          <DashCard>
            <DashCardTitle>
              <Users />
              Testaajat ({allTesters.length})
            </DashCardTitle>
            <TesterList>
              {allTesters.length === 0 && (
                <TesterRow style={{ color: '#555' }}>Ei testaajia vielä</TesterRow>
              )}
              {allTesters.map(t => (
                <TesterRow key={t.id}>
                  <TesterName>{t.name}{t.id === testerId ? ' (sinä)' : ''}</TesterName>
                  <TesterMiniBar>
                    <TesterMiniBarFill $color="#22c55e" $pct={t.total ? (t.green / t.total) * 100 : 0} />
                    <TesterMiniBarFill $color="#facc15" $pct={t.total ? (t.yellow / t.total) * 100 : 0} />
                    <TesterMiniBarFill $color="#ef4444" $pct={t.total ? (t.red / t.total) * 100 : 0} />
                  </TesterMiniBar>
                  <TesterCount>{t.total}</TesterCount>
                </TesterRow>
              ))}
            </TesterList>
          </DashCard>
        </DashboardGrid>

        {sortedReleases.map((release, idx) => {
          const items = batches[release];
          const batch = QA_BATCHES[release];
          const isOpen = expandedBatches[release];
          const batchTested = items.filter(i => myStatus(i.id)).length;
          const batchGreen = items.filter(i => myStatus(i.id) === 'green').length;
          const batchYellow = items.filter(i => myStatus(i.id) === 'yellow').length;
          const batchRed = items.filter(i => myStatus(i.id) === 'red').length;
          const allDone = batchTested === items.length && batchRed === 0 && batchYellow === 0;

          return (
            <BatchCard key={release} $delay={`${idx * 0.05}s`}>
              <BatchHeader onClick={() => toggleBatch(release)}>
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <BatchWeek>{release}</BatchWeek>
                <BatchTitle>{batch?.title || release}</BatchTitle>
                <BatchStats>
                  {allDone ? (
                    <span style={{ color: '#22c55e' }}>{batchTested}/{items.length}</span>
                  ) : (
                    `${batchTested}/${items.length}`
                  )}
                  <MiniBar>
                    <MiniBarFill $color="#22c55e" $pct={items.length ? (batchGreen / items.length) * 100 : 0} />
                    <MiniBarFill $color="#facc15" $pct={items.length ? (batchYellow / items.length) * 100 : 0} />
                    <MiniBarFill $color="#ef4444" $pct={items.length ? (batchRed / items.length) * 100 : 0} />
                  </MiniBar>
                </BatchStats>
              </BatchHeader>

              {isOpen && (
                <BatchBody>
                  {batch?.summary && <BatchSummary>{batch.summary}</BatchSummary>}
                  {batch?.views && (
                    <ViewsList>
                      {batch.views.map(v => (
                        <ViewChip key={v} onClick={() => navigateToView(v)}>
                          {v} <ExternalLink />
                        </ViewChip>
                      ))}
                    </ViewsList>
                  )}
                  {items.map(item => {
                    const status = myStatus(item.id);
                    return (
                      <ItemRow key={item.id}>
                        <StatusDot $status={status} />
                        <ItemTitle
                          $clickable={!!item.routes?.[0]}
                          onClick={() => item.routes?.[0] && navigateToView(item.routes[0])}
                        >
                          {item.title}
                        </ItemTitle>
                        <QuickBtn
                          $variant="green"
                          $active={status === 'green'}
                          onClick={() => handleQuickGreen(item.id)}
                        >
                          OK
                        </QuickBtn>
                        <QuickBtn
                          $variant="yellow"
                          $active={status === 'yellow'}
                          onClick={() => handleOpenModal(item, 'yellow')}
                        >
                          !
                        </QuickBtn>
                        <QuickBtn
                          $variant="red"
                          $active={status === 'red'}
                          onClick={() => handleOpenModal(item, 'red')}
                        >
                          X
                        </QuickBtn>
                        <InfoBtn onClick={() => handleOpenModal(item, null)} title="Ohjeet">
                          <Info />
                        </InfoBtn>
                      </ItemRow>
                    );
                  })}
                </BatchBody>
              )}
            </BatchCard>
          );
        })}
      </Content>

      {modalItem && (
        <QaTestDetailModal
          item={modalItem.item}
          currentStatus={modalItem.presetStatus || myStatus(modalItem.item.id)}
          currentComment={myComment(modalItem.item.id)}
          onSave={saveResult}
          onClose={() => setModalItem(null)}
        />
      )}
    </Container>
  );
}
